import {
  CloseIcon,
  EmptyState,
  Flex,
  SimpleSpinner,
  toast,
} from '@buffer-mono/popcorn'
import { DndContext, DragOverlay, type UniqueIdentifier } from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

import type { MoveIdeaInput } from '~publish/gql/graphql'
import { usePlan } from '~publish/contexts/AccountContext'
import { DROP_ANIMATION } from '~publish/helpers/dndkit/constants'

import { useDeleteIdea } from '../../hooks/useDeleteIdea'
import { IdeaCard } from '../IdeaCard'

import { restrictToRef } from '../../../../helpers/dndkit/restrictToRefModifier'
import { Column } from './Column'
import { NewColumn } from './NewColumn'
import { SortableColumn } from './SortableColumn'
import SortableItem from './SortableItem'
import { createAnnouncements } from './helpers/createAnnouncements'
import { UNASSIGNED_GROUP_ID, UNASSIGNED_GROUP_NAME } from './helpers/helpers'
import { useBoardData } from './hooks/useBoardData'
import { useCreateIdeaGroup } from './hooks/useCreateIdeaGroup'
import { useDndBoard } from './hooks/useDndBoard'
import { useMoveIdea } from './hooks/useMoveIdea'
import { useMoveIdeaGroup } from './hooks/useMoveIdeaGroup'

import { useIdeaComposer } from '~publish/components/IdeaManagementRouter'
import { CreateUploadDropzone } from '../CreateUploadDropzone'

import styles from './Board.module.css'
import { useIdeasMultiSelect } from '../IdeasMultiSelect'

type BoardProps = {
  onDrop?: (files: File[], groupId?: string) => void
  onPaste?: (files: File[], groupId?: string) => void
}

const MAX_FREE_GROUPS = 3

export const Board = ({ onDrop, onPaste }: BoardProps): JSX.Element => {
  const boardRef = useRef<HTMLDivElement>(null)
  const { editIdeaWithComposer } = useIdeaComposer()
  const { data, loading, error } = useBoardData()
  const { deleteIdea } = useDeleteIdea()
  const moveIdea = useMoveIdea()
  const moveGroup = useMoveIdeaGroup()
  const createIdeaGroup = useCreateIdeaGroup()
  const { selectedIds, selectIdea, selecting } = useIdeasMultiSelect()
  const plan = usePlan()

  const handleCreateColumn = async (name: string): Promise<void> => {
    try {
      await createIdeaGroup(name)
      toast.success(`Group ${name} created successfully`)
    } catch (error) {
      toast.error(`Failed to create group ${name}. Please try again later.`)
    }
  }

  async function handleItemMoved({
    groupId,
    ideaId,
    placeAfterId,
  }: MoveIdeaInput): Promise<void> {
    try {
      await moveIdea({
        groupId,
        ideaId,
        placeAfterId:
          placeAfterId === UNASSIGNED_GROUP_ID ? undefined : placeAfterId,
      })
    } catch (error: unknown) {
      const message = (error as Error).message ?? 'Please try again later'
      toast.error(`Failed to move idea. ${message}.`)
      throw error
    }
  }

  async function handleColumnMoved({
    id,
    placeAfterId,
  }: {
    id: string
    placeAfterId?: string
  }): Promise<void> {
    try {
      await moveGroup(
        id,
        placeAfterId === UNASSIGNED_GROUP_ID ? undefined : placeAfterId,
      )
    } catch (error) {
      toast.error('Failed to move group. Please try again later.')
      throw error
    }
  }

  const {
    activeId: activeDraggedId,
    items,
    columns,
    lockOverlayId,
    updateItems,
    contextProps,
  } = useDndBoard(
    data.itemsMap ?? {},
    handleItemMoved,
    handleColumnMoved,
    isGroupLocked,
  )

  useEffect(() => {
    updateItems(data.itemsMap)
  }, [data.itemsMap, updateItems])

  function isGroup(id: UniqueIdentifier): boolean {
    return data.groups.some((group) => group.id === id)
  }

  function isGroupLocked(id: UniqueIdentifier): boolean {
    return data.groups.find((group) => group.id === id)?.isLocked ?? false
  }

  function isIdea(id: UniqueIdentifier): boolean {
    return data.ideas.some((idea) => idea.id === id)
  }

  // TODO: add skeleton loading state
  if (loading) {
    return (
      <Flex align="center" justify="center">
        <SimpleSpinner size="medium" role="progressbar" />
      </Flex>
    )
  }

  if (error) {
    return (
      <EmptyState variant="critical">
        <EmptyState.Icon>
          <CloseIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Error while loading ideas</EmptyState.Heading>
        <EmptyState.Description>
          Try refreshing a page, let us know if error persists
        </EmptyState.Description>
      </EmptyState>
    )
  }

  const renderIdea = (id: UniqueIdentifier): JSX.Element | null => {
    const idea = data.ideas.find((idea) => idea.id === id)
    if (!idea) return null
    return (
      <IdeaCard
        idea={idea}
        variant="compact"
        onOpen={(idea): void => {
          editIdeaWithComposer({
            source: 'create-board-ideaCard-card-1',
            ideaId: idea.id,
          })
        }}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error Idea and IdeaFragment are not compatible because of null
        onDelete={deleteIdea}
        selectable={selecting}
        selected={selectedIds.includes(idea.id)}
        onSelect={selectIdea}
        onMoveIntoGroup={(ideaId, groupId): Promise<void> =>
          handleItemMoved({ ideaId, groupId })
        }
      />
    )
  }

  const columnsToRender = columns.filter((id) =>
    data.groups.some((g) => g.id === id),
  )
  const sortableColumns = columnsToRender.filter((id) => !isGroupLocked(id))
  const isGroupLimitReached =
    plan.id === 'free' && columnsToRender.length >= MAX_FREE_GROUPS

  return (
    <DndContext
      autoScroll={true}
      {...contextProps}
      // This is a custom modifier to restrict the drag to the board container only
      modifiers={[restrictToRef(boardRef)]}
      accessibility={{
        announcements: createAnnouncements(data.groups, data.ideas),
      }}
    >
      <div className={styles.wrapper} ref={boardRef}>
        {/* Unassigned column */}
        <SortableColumn id={UNASSIGNED_GROUP_ID} disabled={true}>
          <CreateUploadDropzone
            onDrop={(files: File[]): void =>
              onDrop?.(files, `${UNASSIGNED_GROUP_ID}`)
            }
            onPaste={(files: File[]): void =>
              onPaste?.(files, `${UNASSIGNED_GROUP_ID}`)
            }
          >
            <Column
              id={UNASSIGNED_GROUP_ID}
              name={UNASSIGNED_GROUP_NAME}
              editable={false}
              items={items[UNASSIGNED_GROUP_ID] ?? []}
              renderItem={renderIdea}
            />
          </CreateUploadDropzone>
        </SortableColumn>
        {/* Sortable column */}
        {columnsToRender.length > 0 && (
          <SortableContext
            id="board"
            items={sortableColumns}
            strategy={horizontalListSortingStrategy}
          >
            {columnsToRender.map((id) => (
              <SortableColumn id={id} key={id} disabled={isGroupLocked(id)}>
                <CreateUploadDropzone
                  disabled={isGroupLocked(id)}
                  onDrop={(files: File[]): void => onDrop?.(files, `${id}`)}
                  onPaste={(files: File[]): void => onPaste?.(files, `${id}`)}
                >
                  <Column
                    id={id}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    name={data.groups.find((g) => g.id === id)!.name}
                    items={items[id] ?? []}
                    renderItem={renderIdea}
                    locked={isGroupLocked(id)}
                    showLockOverlay={isGroupLocked(id) && lockOverlayId === id}
                  />
                </CreateUploadDropzone>
              </SortableColumn>
            ))}
          </SortableContext>
        )}

        {/* Create new column */}
        <NewColumn
          onCreate={handleCreateColumn}
          limitReached={isGroupLimitReached}
        />
      </div>

      {createPortal(
        <DragOverlay
          dropAnimation={DROP_ANIMATION}
          className={styles.dragOverlay}
        >
          {/* Render dragged column */}
          {activeDraggedId && isGroup(activeDraggedId) && (
            <SortableColumn id={activeDraggedId} dragOverlay={true}>
              <Column
                id={activeDraggedId}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                name={data.groups.find((g) => g.id === activeDraggedId)!.name}
                items={items[activeDraggedId]}
                renderItem={renderIdea}
              />
            </SortableColumn>
          )}

          {/* Render dragged idea */}
          {activeDraggedId && isIdea(activeDraggedId) && (
            <SortableItem id={activeDraggedId} dragOverlay={true}>
              {renderIdea(activeDraggedId)}
            </SortableItem>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  )
}
